import React, { useContext, useMemo } from 'react'
import {
  Typography,
  Chip,
  Box,
  Grid,
  Paper,
  useTheme,
  Hidden,
  styledComponent,
  CheckboxCircle,
  PlayCircleIcon,
  type SxProps,
  SvgIcon,
  Rating,
  useMediaQuery,
  WhiteTooltip
} from '@hermes/web-components'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import { mergeStatisticByName } from '../../../../utils/statistic'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { AppDataContext } from '../../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { useGetSearchProfileData } from '../../../../hooks/useGetProfileData'
import { useDateTimeCorrector } from '../../../../hooks/useDatetimeCorrector'
import { PracticeOrSpecialistProfile, StatisticTreatment } from '../../../../types'
import { GridHidden } from '../../../GridHidden'
import { useGetUnitsByLocale } from '../../../../hooks/useGetUnitsByLocale'
import useStatistic from '../../../../hooks/useStatistic'
import { AMOUNT_KEYWORDS, AMOUNT_KEYWORDS_WPA, AppointmentType, FilterType } from '../../../../constants/search'
import { getAddressFromPractices, transformAddressToString } from '../../../../utils/address'
import getChipColorConfiguration from '../../../../utils/getChipColorConfiguration'
import { StyledPlayVideoButton } from '../../../profiles/Intro/styles'
import Avatar from '../../../Avatar'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import { useVisibilityComponents } from '../../../../hooks/useVisibilityComponents'
import FeeAgreedBadge from '../../../Icons/FeeAgreedBadge'
import FeeAgreedBadgeReversed from '../../../Icons/FeeAgreedBadgeReversed'
import { useWpaTenant } from '../../../../hooks/useWpaTenant'
import SearchAppointmentModal from '../../../search/SearchAppointmentModal'
import { getDefaultCountry } from '../../../../constants/global'

const TopKeywordText = styledComponent(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  fontWeight: 500,
  color: theme.palette.text.darkNavy
}))

const StyledNumber = styledComponent(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h2,
    fontSize: theme.typography.h2.fontSize,
    fontWeight: 900
  },
  fontWeight: 900,
  marginRight: 4,
  color: theme.palette.text.darkNavy
}))

const StyledNumberDescription = styledComponent(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  whiteSpace: 'pre-line',
  fontWeight: 400,
  textTransform: 'uppercase',
  lineHeight: 1.4,
  color: theme.palette.text.darkNavy
}))

const StyledAvatar = styledComponent(Avatar)(({ theme }) => ({
  width: '120px',
  height: '120px',
  [theme.breakpoints.down('sm')]: {
    width: '92px',
    height: '92px'
  },
  ...(theme?.custom?.SearchProfileCard?.avatar ?? {})
}))

const TrustRatingStatistic = ({ count, name }: { count?: number; name?: string }) => (
  <Grid item container alignItems="center" sm>
    {!!count && (
      <>
        <StyledNumber>{count}</StyledNumber>
        <StyledNumberDescription>{name}</StyledNumberDescription>
      </>
    )}
  </Grid>
)
const FeeAgreedTooltip = ({ children }: { children: React.ReactNode }) => {
  const translate = useTranslateMessage()

  return (
    <WhiteTooltip title={translate('filter.fee-agreed.description')}>
      <Box>{children}</Box>
    </WhiteTooltip>
  )
}

const PremiumTooltip = ({ children }: { children: React.ReactNode }) => {
  const translate = useTranslateMessage()

  return (
    <WhiteTooltip title={translate('filter.premium.description')}>
      <Box>{children}</Box>
    </WhiteTooltip>
  )
}

const Badge = ({
  icon,
  title,
  color,
  textStyles,
  containerStyles
}: {
  icon: JSX.Element
  title?: string
  color?: string
  textStyles?: SxProps
  containerStyles?: SxProps
}) => (
  <Grid container gap="6px" width="auto" alignItems="center" sx={containerStyles}>
    {icon}
    <Typography fontSize="13px" lineHeight="20px" color={color || 'system.navy900'} sx={textStyles}>
      {title}
    </Typography>
  </Grid>
)

// todo: fix complexity
// eslint-disable-next-line complexity
function SearchProfileCard({ specialist, practice, handleSetVideoPlayerModalData }: PracticeOrSpecialistProfile) {
  const introductionVideo = (specialist || practice).media?.[0]
  const visibilityComponents = useVisibilityComponents({ specialist, practice })
  const { isWPA } = useWpaTenant()
  const translate = useTranslateMessage()
  const theme = useTheme()
  const router = useRouter()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { language, locale } = useContext(AppDataContext)
  const defaultCountry = getDefaultCountry(locale)
  const {
    state: {
      filters: { treatment, selectedKeyword, appointmentType, distance: filterDistance },
      specialty,
      practices: practicesFromState
    }
  } = useContext(SearchProfilesPageContext)

  const profileData = useGetSearchProfileData({
    ...(practice ? { practice } : { specialist }),
    language,
    specialty
  })

  const {
    latestReviewPractice,
    procedures,
    profileAvatarLink,
    profileAbbreviation,
    averageRating,
    practices,
    distance,
    shouldShowContactButton,
    isUnclaimed,
    latestReview,
    profileFullName,
    profileReviewTotal,
    haveVideoConsultation,
    topLevelKeyword,
    transformedAddress,
    yearsOfExperienceValue,
    isPractice,
    externalId,
    slug
  } = profileData

  const handleOpenIntroductionVideoPlayer = () => {
    window.dataLayer.push({
      event: 'src_pg_prf_vid_click',
      cont_prf_name: profileFullName,
      cont_uuid: externalId,
      cont_prf_type: isPractice ? 'Practice' : 'Specialist'
    })
    handleSetVideoPlayerModalData({
      url: introductionVideo?.link,
      practiceId: practice?.id,
      specialistId: specialist?.id,
      mediaId: introductionVideo?.id
    })
  }

  const getUnitsByLocale = useGetUnitsByLocale()

  const getLocationString = (separator: string) => {
    const addressToTransform = getAddressFromPractices(practices, true)
    const isOnlyVideoConsultation = practices.length === 1 && practices[0].slug === FilterType.VIDEO_CONSULTATION

    const filterDistanceNumber = Number(filterDistance)

    if (
      appointmentType === AppointmentType.VIDEO_CONSULTATION ||
      isOnlyVideoConsultation ||
      (haveVideoConsultation && filterDistanceNumber < distance)
    ) {
      return translate('videoConsultation.availableOnline')
    }

    if (!addressToTransform) {
      return ''
    }

    return `${getUnitsByLocale(distance, 2).distanceWithUnits} ${separator} ${transformAddressToString(
      addressToTransform,
      language
    )}`
  }

  const dateTime = useDateTimeCorrector(latestReview?.createdAt)

  const unverifiedIcon = (
    <Box
      borderRadius={5}
      bgcolor={isWPA ? 'text.black700' : 'system.coral200'}
      p={0.5}
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="22px"
      height="22px"
    >
      <SvgIcon sx={{ height: 22, width: 22, color: isWPA ? '#fff' : '#051851' }}>
        <use href="#unverified-icon" />
      </SvgIcon>
    </Box>
  )

  const { mergedStatistic, reviewStatistic } = useStatistic(specialist?.statistic || practice?.statistic || [])
  const mergedStatisticByName = specialist
    ? mergedStatistic.reduce(mergeStatisticByName(language), [])
    : reviewStatistic.reduce(mergeStatisticByName(language), [])
  const activeRelatedKeyword = specialty
  const altRelatedKeyword =
    (specialist || practice)?.statistic.find((s) => s.tagId === activeRelatedKeyword.id)?.tagName[language] || ''

  const treatments: StatisticTreatment[] = [
    ...((treatment && selectedKeyword && !practicesFromState.length) || altRelatedKeyword
      ? [
          {
            label: selectedKeyword || altRelatedKeyword,
            variant: 'primary'
          }
        ]
      : []),
    ...mergedStatisticByName
      .filter((keyword) => keyword.tagName[language] !== selectedKeyword && keyword.tagId !== specialty.id)
      .map((s) => ({ label: s.tagName[language], variant: 'secondary' }))
  ].slice(0, isWPA ? AMOUNT_KEYWORDS_WPA : AMOUNT_KEYWORDS)

  const lengthKeywords = specialist?.statisticLength || practice?.statisticLength || 0

  const latestReviewPracticeLink = latestReviewPractice
    ? `/${locale}/practice/${specialist?.lastReview?.practice.slug}`
    : ''

  const link = practice ? `/${locale}/practice/${practice?.slug}` : `/${locale}/specialist/${specialist?.slug}`
  const linkReviewsPage = profileReviewTotal > 10 && !isWPA ? '/reviews/page-2' : '#reviews'
  const linkReviews = practice
    ? `/${locale}/practice/${practice?.slug}${linkReviewsPage}`
    : `/${locale}/specialist/${specialist?.slug}${linkReviewsPage}`

  if (lengthKeywords > 0) {
    treatments.push({
      variant: 'other',
      label: `+${lengthKeywords}`,
      href: link
    })
  }

  const textDisabledStr = useMemo(() => 'text.disabled', [])
  const saveActiveKeywordInStorage = () => {
    if (activeRelatedKeyword.id !== 1 && activeRelatedKeyword?.name?.[language]) {
      localStorage.setItem(`activeKeyword-${slug}`, activeRelatedKeyword.name[language])
    }
  }

  const renderTreatment = useMemo(
    () => () =>
      treatments.map((keyword) => {
        const additionalColorStyle = getChipColorConfiguration(keyword.variant)
        return (
          <Grid item key={keyword.label}>
            {/* @ts-ignore */}
            <Chip
              {...keyword}
              icon={
                keyword.variant === 'primary' ? (
                  <CheckboxCircle
                    sx={{ marginLeft: '0 !important', marginRight: '0 !important', width: '20px', height: '20px' }}
                  />
                ) : (
                  <></>
                )
              }
              sx={{
                maxWidth: 250,
                fontWeight: '500',
                cursor: keyword.href ? 'pointer' : null,
                '&:hover': {
                  background: theme.palette.system[`${keyword.variant}200`]
                },
                ...(theme?.custom?.common?.treatmentChip ?? {}),
                ...additionalColorStyle,
                color: theme.custom?.SpecialistPage?.common?.background ?? 'primary.dark'
              }}
              component={keyword.href ? 'a' : 'div'}
            />
          </Grid>
        )
      }),
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [treatment, procedures]
  )

  const renderRatePaper = () => (
    <Grid width="100%">
      {(specialist?.peerRecommendationsCount || !!averageRating) && (
        <Paper
          sx={{
            width: '100%',
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow:
              '0px 12px 17px rgba(2, 3, 3, 0.03), 0px 5px 22px rgba(2, 3, 3, 0.02), 0px 7px 8px rgba(2, 3, 3, 0.04)',
            backgroundColor: 'background.default',
            cursor: profileReviewTotal > 0 ? 'pointer' : 'auto',
            ...(theme?.custom?.SearchProfileCard?.rateContainer ?? {})
          }}
          onClick={() => profileReviewTotal > 0 && window.open(linkReviews, '_blank')}
        >
          <Grid container>
            {!!averageRating && !isUnclaimed && (
              <Grid item container xs={5} sm={12} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography
                    fontWeight="900"
                    fontFamily="ES Klarheit Kurrent"
                    fontSize={38.49}
                    lineHeight="48px"
                    color="text.darkNavy"
                  >
                    {averageRating?.toFixed?.(2)}
                  </Typography>
                </Grid>
                <Grid container item xs={12} sm>
                  <Grid item xs={12}>
                    <Rating value={Number(averageRating)} size="small" sx={{ fontSize: 16 }} readOnly />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" lineHeight={1} color="text.darkNavy">
                      {translate('reviews.patient_trust_rating')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item container xs={7} sm={12}>
              {!!averageRating && !isUnclaimed && (
                <TrustRatingStatistic
                  count={profileReviewTotal}
                  name={translate('patient_reviews', { count: profileReviewTotal })}
                />
              )}
              <TrustRatingStatistic
                count={specialist?.peerRecommendationsCount}
                name={
                  specialist?.peerRecommendationsCount
                    ? translate('search.peer_endorsements', { count: specialist?.peerRecommendationsCount })
                    : undefined
                }
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      {shouldShowContactButton && (
        <GridHidden down="lg" item xs>
          <GridHidden down="lg" item xs>
            <SearchAppointmentModal
              isUnclaimed={isUnclaimed}
              totalReviews={profileReviewTotal}
              specialist={specialist}
              practice={practice}
              profileFullName={profileFullName}
              isPractice={isPractice}
              externalId={externalId}
            />
          </GridHidden>
        </GridHidden>
      )}
    </Grid>
  )

  return (
    <Grid
      container
      width="100%"
      wrap="nowrap"
      gap="16px"
      data-testid={TEST_IDS.SEARCH_RESULT_CARD}
      sx={{
        height: 'fit-content',
        padding: 2,
        border: `1px solid ${theme.palette.grey['600']}`,
        borderRadius: '4px',
        transition: '0.1s linear',
        '&:hover': {
          border: `1px solid ${theme.palette.blue['400']}`
        },
        ...(theme?.custom?.SearchProfileCard?.container ?? {})
      }}
    >
      <Grid container sx={{ ...(theme?.custom?.SearchProfileCard?.content ?? {}) }}>
        <Grid
          container
          wrap="nowrap"
          gap="12px"
          onAuxClick={(e) => {
            if (e.button === 1) {
              saveActiveKeywordInStorage()
              window.open(link, '_blank')
            }
          }}
          sx={{ cursor: 'pointer', position: 'relative' }}
        >
          <Box>
            <Box
              sx={{ ...(theme?.custom?.SearchProfileCard?.avatarContainer ?? {}) }}
              height="min-content"
              position="relative"
            >
              <StyledAvatar
                onClick={() => {
                  saveActiveKeywordInStorage()
                  router.push(link)
                }}
                alt={profileFullName}
                data-testid={TEST_IDS.SEARCH_RESULT_CARD_AVATAR}
                width={92}
                height={92}
                preload
                src={profileAvatarLink}
              >
                {profileAbbreviation}
              </StyledAvatar>
              {visibilityComponents.introductionVideoAvailable && !!introductionVideo?.link.length ? (
                <StyledPlayVideoButton variant="ghost" onClick={handleOpenIntroductionVideoPlayer}>
                  <PlayCircleIcon sx={{ width: '32px', height: '32px' }} />
                </StyledPlayVideoButton>
              ) : null}
            </Box>
            <Hidden lgUp>
              {specialist?.customFields?.feeAgreed && isWPA && (
                <Badge
                  icon={
                    isMobile ? (
                      <FeeAgreedTooltip>
                        <FeeAgreedBadgeReversed
                          sx={{
                            width: '105px',
                            height: '35px',
                            ...(theme?.custom?.SearchProfileCard?.mobileBadge ?? {})
                          }}
                        />
                      </FeeAgreedTooltip>
                    ) : (
                      <FeeAgreedTooltip>
                        <FeeAgreedBadge
                          sx={{
                            width: '125px',
                            height: '35px',
                            ...(theme?.custom?.SearchProfileCard?.mobileBadge ?? {})
                          }}
                        />
                      </FeeAgreedTooltip>
                    )
                  }
                  title={''}
                />
              )}
              {practice?.customFields?.premium && isWPA && (
                <PremiumTooltip>
                  <Badge
                    icon={
                      <svg
                        style={{
                          width: '105px',
                          height: '33px',
                          ...(theme?.custom?.SearchProfileCard?.mobileBadge ?? {})
                        }}
                      >
                        <use href="#premium-badge-lined-icon" />
                      </svg>
                    }
                    title={''}
                  />
                </PremiumTooltip>
              )}
            </Hidden>
          </Box>
          <Grid onClick={() => router.push(link)} item container direction="column" gap={{ xs: '2px', lg: '6px' }}>
            <NextLink
              href={link}
              onMouseDown={saveActiveKeywordInStorage}
              style={{ textDecoration: 'none', width: '100%' }}
            >
              <Typography
                fontWeight={700}
                fontSize="24px"
                lineHeight="32px"
                fontFamily="ES Klarheit Kurrent"
                color="text.darkNavy"
                data-testid={TEST_IDS.SEARCH_RESULT_CARD_FULL_NAME}
                component="h2"
                sx={{
                  width:
                    (specialist?.customFields?.feeAgreed || practice?.customFields?.premium) && !isMobile
                      ? 'calc(100% - 150px)'
                      : '100%'
                }}
              >
                {profileFullName}
              </Typography>
            </NextLink>
            <Box
              display="flex"
              alignItems={{ xs: 'start', lg: 'center' }}
              flexDirection={{ xs: 'column', lg: 'row' }}
              data-testid={TEST_IDS.SEARCH_RESULT_CARD_KEYWORD}
            >
              {!practice && <TopKeywordText>{topLevelKeyword}</TopKeywordText>}
            </Box>
            {!practice && specialist?.connectionsCount > 20 && !isWPA && (
              <Badge
                containerStyles={{
                  width: '100%'
                }}
                icon={
                  <SvgIcon sx={{ width: '16px', height: '16px' }}>
                    <use href="#connections-badge-icon" />
                  </SvgIcon>
                }
                data-testid={TEST_IDS.SEARCH_RESULT_CARD_BADGE}
                title={translate('profile.intro.trustedNetwork')}
              />
            )}
            <Grid container sx={theme?.custom?.SearchBar?.searchResultBadgeContainer} direction="row">
              {!!yearsOfExperienceValue && (
                <Badge
                  icon={
                    <SvgIcon sx={{ width: '16px', height: '16px' }}>
                      <use href="#person-with-shield-icon" />
                    </SvgIcon>
                  }
                  color={theme?.custom?.common?.badgeTextColor}
                  title={translate('search.profileCard.badge.yearsOfExperience', {
                    years: yearsOfExperienceValue
                  })}
                />
              )}
              {isUnclaimed ? (
                <Badge icon={unverifiedIcon} title={translate('unverivied_profile')} />
              ) : (
                <Badge
                  icon={
                    <SvgIcon sx={{ width: '16px', height: '16px' }} viewBox="0 0 16 17">
                      <use href="#checkbox-circle-outline-icon" />
                    </SvgIcon>
                  }
                  color={theme?.custom?.common?.badgeTextColor}
                  title={
                    isPractice
                      ? translate('verified_profile')
                      : translate('search.profileCard.badge.verifiedPractitioner')
                  }
                />
              )}
            </Grid>
          </Grid>
          <Hidden lgDown>
            {specialist?.customFields?.feeAgreed && isWPA && (
              <Badge
                containerStyles={{ position: 'absolute', right: 0, top: 0 }}
                icon={
                  <FeeAgreedTooltip>
                    <FeeAgreedBadge sx={{ width: '100px', height: '30px' }} />
                  </FeeAgreedTooltip>
                }
                title={''}
              />
            )}
            {practice?.customFields?.premium && isWPA && (
              <Badge
                containerStyles={{ position: 'absolute', right: 0, top: 0 }}
                icon={
                  <PremiumTooltip>
                    <svg style={{ width: '124px', height: '35px' }}>
                      <use href="#premium-badge-lined-icon" />
                    </svg>
                  </PremiumTooltip>
                }
                title={''}
              />
            )}
          </Hidden>
        </Grid>
        {specialist ? (
          <Typography
            data-testid={TEST_IDS.SEARCH_RESULT_CARD_LOCATION}
            variant="body2"
            sx={{ ...(theme?.custom?.SearchProfileCard?.locationText ?? { color: '#2C3341' }) }}
          >
            {getLocationString('|')}
          </Typography>
        ) : null}
        {practice ? (
          <Typography variant="body2" color="#2C3341" textTransform="capitalize">
            {transformedAddress || defaultCountry}
          </Typography>
        ) : null}
        <Grid component={Hidden} container width="100%" lgUp implementation="css">
          {renderRatePaper()}
        </Grid>
        {!!treatments.length && !(practice?.basic || specialist?.basic) && (
          <Grid my={1} container alignItems="center" data-testid={TEST_IDS.SEARCH_RESULT_CARD_TREATMENTS} gap={1}>
            <Typography variant="caption" color="text.black900">
              {translate('peerRecommendation.recommended_for')}
            </Typography>
            {renderTreatment()}
          </Grid>
        )}
        {shouldShowContactButton && (
          <GridHidden up="lg" item xs data-testid={TEST_IDS.SEARCH_RESULT_CARD_CONTACTS}>
            <SearchAppointmentModal
              isUnclaimed={isUnclaimed}
              totalReviews={profileReviewTotal}
              practice={practice}
              specialist={specialist}
              profileFullName={profileFullName}
              isPractice={isPractice}
              externalId={externalId}
            />
          </GridHidden>
        )}
        {!isUnclaimed && !!latestReview && (
          <GridHidden container down="lg">
            <Typography
              variant="body2"
              sx={{
                maxHeight: 65,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '3',
                WebkitBoxOrient: 'vertical'
              }}
              color={theme?.custom?.common?.disabledTextColor ?? textDisabledStr}
              textOverflow="ellipsis"
            >
              {latestReview.text}
            </Typography>
            <Grid container alignItems="center" sx={{ mt: 0.5 }}>
              <Typography
                variant="subtitle2"
                color={theme?.custom?.common?.disabledTextColor ?? textDisabledStr}
                sx={{ mr: 2 }}
              >
                {dateTime}
              </Typography>
              <SvgIcon
                sx={{
                  width: '16px',
                  height: '16px'
                }}
                viewBox="0 0 16 16"
              >
                <use href="#user-verified-icon" />
              </SvgIcon>
              <Typography
                variant="subtitle2"
                color={theme?.custom?.common?.disabledTextColor ?? textDisabledStr}
                sx={{ ml: 0.5, mr: 2 }}
                textTransform="lowercase"
              >
                {translate('verified_patient')}
              </Typography>
              {!!latestReviewPractice && (
                <Box
                  flexDirection="row"
                  display="flex"
                  sx={{ cursor: 'pointer' }}
                  alignItems="center"
                  onClick={() => router.push(latestReviewPracticeLink)}
                >
                  <SvgIcon sx={{ width: '16px', mr: 0.25, color: 'background.primary' }} viewBox="0 0 16 16">
                    <use href="#location-on-icon" />
                  </SvgIcon>
                  <Typography
                    variant="subtitle2"
                    color={theme?.custom?.common?.disabledTextColor ?? textDisabledStr}
                    textTransform="capitalize"
                  >
                    {latestReviewPractice}
                  </Typography>
                </Box>
              )}
            </Grid>
          </GridHidden>
        )}
      </Grid>
      <Grid
        component={Hidden}
        sx={{
          flexBasis: '45%'
        }}
        lgDown
        implementation="css"
      >
        {renderRatePaper()}
      </Grid>
    </Grid>
  )
}

export default SearchProfileCard
